import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert, Button, Spinner, Badge } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';

import { OperatorMessageModel } from './OperatorMessageModel';

interface Props {
  workflowId: number | string | null;
}

/**
 * Keyed by id, because a model reads one workflow for its lifetime. An id that is not a positive integer names no
 * workflow, so nothing is shown.
 */
const OperatorMessagePanel = ({ workflowId }: Props) => {
  const id = Number(workflowId);
  return Number.isInteger(id) && id > 0 ? <OperatorMessage key={id} workflowId={id} /> : null;
};

/** A pure view over {@link OperatorMessageModel}. */
const OperatorMessage = observer(({ workflowId }: { workflowId: number }) => {
  const dispatch = useAppDispatch();
  const [model] = useState(() => new OperatorMessageModel(workflowId, dispatch));

  const message = model.message;
  if (message === 'loading') {
    return (
      <div className="d-flex align-items-center gap-2 py-3">
        <Spinner animation="border" size="sm" />
        <span>Loading operator message…</span>
      </div>
    );
  }
  if (message === 'not-found') return null;
  if (message instanceof Error) return <Alert variant="danger">Failed to load operator message.</Alert>;

  const outcome = model.outcome;
  const pendingActions = message.workflows_actions.filter((action) => !action.response);
  const respondedActions = message.workflows_actions.filter((action) => !!action.response);

  return (
    <div className="mt-3">
      {outcome instanceof Error && (
        <Alert variant="danger" dismissible onClose={() => model.dismissOutcome()}>
          {outcome.message}
        </Alert>
      )}
      {outcome === 'responded' && (
        <Alert variant="success" dismissible onClose={() => model.dismissOutcome()}>
          Response submitted successfully.
        </Alert>
      )}

      <div className="mb-3">
        <strong>Subject:</strong> {message.workflow_subject}
      </div>
      {pendingActions.map((action) => (
        <div key={action.workflow_action_id} className="border rounded p-3 mb-3">
          <div className="mb-1">
            <strong>{action.subject}</strong>
          </div>
          {action.message && (
            <p className="text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
              {action.message}
            </p>
          )}
          {action.response_options.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {action.response_options.map((option) => (
                <Button
                  key={option.id}
                  variant="outline-primary"
                  size="sm"
                  disabled={model.busy}
                  onClick={() => model.respond(action.workflow_action_id, option.id)}
                >
                  {model.responding === action.workflow_action_id && <Spinner animation="border" size="sm" className="me-2" />}
                  {option.label}
                </Button>
              ))}
            </div>
          ) : (
            <span className="text-muted fst-italic">No response options available.</span>
          )}
        </div>
      ))}
      {respondedActions.map((action) => (
        <div key={action.workflow_action_id} className="border rounded p-3 mb-3 bg-light">
          <div className="mb-1 d-flex align-items-center gap-2">
            <strong>{action.subject}</strong>
            <Badge bg="success">Responded</Badge>
          </div>
          {action.message && (
            <p className="text-muted mb-2" style={{ whiteSpace: 'pre-wrap' }}>
              {action.message}
            </p>
          )}
          <div className="text-muted">
            <span className="fw-medium">Response: </span>
            {action.response?.label}
            {action.response?.user && <span className="ms-2 fst-italic text-secondary">— {action.response.user}</span>}
          </div>
        </div>
      ))}
    </div>
  );
});

export default OperatorMessagePanel;
